import { useInfiniteQuery } from "@tanstack/react-query";
import axios from "axios";
import { useState, useMemo } from "react";
import { Accordion, Badge, Card, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowDown, faArrowUp, faAdjust, faHistory, faStore, faUser, faCalendar } from "@fortawesome/free-solid-svg-icons";
import { useEmpresas } from "providers/EmpresaProvider";
import { useBreakpoints } from "providers/BreakpointsProvider";
import { FixedSizeList as List } from "react-window";
import InfiniteLoader from "react-window-infinite-loader";

interface MovimentoEstoque {
    id: number;
    produtoId: number;
    empresaId: number;
    empresaNome: string;
    data: string;
    quantidade: number;
    quantidadeAnterior: number;
    quantidadeNova: number;
    lote?: string;
    tipo: TipoMovimento;
    tipoDescricao: string;
    usuarioNome: string;
}

interface MovimentoEstoqueResponse {
    data: MovimentoEstoque[];
    total: number;
    hasMore: boolean;
    isSuccess: boolean;
    message: string;
}

enum TipoMovimento {
    Entrada = 0,
    Saida = 1,
    Ajuste = 2
}

interface HistoricoEstoqueTabProps {
    produtoId: number;
}

interface ItemProps {
    index: number;
    style: React.CSSProperties;
    data: {
        items: MovimentoEstoque[];
        loadMoreItems: () => void;
        hasNextPage: boolean;
        isLoading: boolean;
    };
}

const MovimentoRow: React.FC<ItemProps> = ({ index, style, data }) => {
    const { items, loadMoreItems, hasNextPage, isLoading } = data;
    const { breakpoints } = useBreakpoints();
    const isMobile = breakpoints.down('md');

    // Se estamos próximos do fim e ainda há mais itens para carregar
    const isItemLoaded = (index: number) => !!items[index];

    if (!isItemLoaded(index) && hasNextPage) {
        if (index === items.length) {
            loadMoreItems();
            return (
                <div style={style} className="d-flex justify-content-center align-items-center">
                    <Spinner animation="border" size="sm" />
                    <span className="ms-2">Carregando...</span>
                </div>
            );
        }
        return null;
    }

    const movimento = items[index];
    movimento.quantidade = movimento.quantidadeNova - movimento.quantidadeAnterior;
    if (!movimento) return null;

    const getIconByType = (tipo: TipoMovimento) => {
        switch (tipo) {
            case TipoMovimento.Entrada:
                return <FontAwesomeIcon icon={faArrowUp} className="text-success" />;
            case TipoMovimento.Saida:
                return <FontAwesomeIcon icon={faArrowDown} className="text-danger" />;
            case TipoMovimento.Ajuste:
                return <FontAwesomeIcon icon={faAdjust} className="text-warning" />;
            default:
                return <FontAwesomeIcon icon={faHistory} className="text-body-tertiary" />;
        }
    };

    const getBadgeVariant = (tipo: TipoMovimento) => {
        switch (tipo) {
            case TipoMovimento.Entrada:
                return "success";
            case TipoMovimento.Saida:
                return "danger";
            case TipoMovimento.Ajuste:
                return "warning";
            default:
                return "secondary";
        }
    };

    const getQuantidade = () => {
        return <span
            className={
                movimento.quantidade > 0
                    ? "text-success fw-bold"
                    : movimento.quantidade < 0
                        ? "text-danger fw-bold"
                        : "text-body-tertiary"
            }
        >
            {movimento.quantidade > 0 ? "+" : ""}
            {movimento.quantidade}
        </span>
    }

    if (isMobile) {
        return (
            <div style={style} className="px-3 py-2 border-bottom border-translucent">
                <div className="d-flex justify-content-between align-items-start mb-2">
                    <div className="d-flex align-items-center gap-2">
                        {getIconByType(movimento.tipo)}
                        <Badge bg={getBadgeVariant(movimento.tipo)} pill className="fs-9">
                            {movimento.tipoDescricao}
                        </Badge>
                    </div>
                    <small className="text-body-tertiary">
                        <FontAwesomeIcon icon={faCalendar} className="me-1" />
                        {new Date(movimento.data).toLocaleDateString('pt-BR', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric'
                        })}
                    </small>
                </div>

                <div className="row g-2 mb-2">
                    <div className="col-6">
                        <small className="text-body-secondary d-block">Quantidade</small>
                        {getQuantidade()}
                    </div>
                    <div className="col-6">
                        <small className="text-body-secondary d-block">Saldo</small>
                        <span className="fw-bold">{movimento.quantidadeNova}</span>
                    </div>
                </div>

                <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center gap-1">
                        <FontAwesomeIcon icon={faStore} className="text-body-tertiary" size="sm" />
                        <small>{movimento.empresaNome}</small>
                    </div>
                    <div className="d-flex align-items-center gap-1">
                        <FontAwesomeIcon icon={faUser} className="text-body-tertiary" size="sm" />
                        <small className="text-body-tertiary">{movimento.usuarioNome}</small>
                    </div>
                </div>

                {movimento.lote && (
                    <div className="mt-1">
                        <small className="text-body-tertiary">Lote: {movimento.lote}</small>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div style={style} className="d-flex align-items-center border-bottom border-translucent px-3 py-2">
            <div className="flex-shrink-0" style={{ width: "120px" }}>
                <small>
                    {new Date(movimento.data).toLocaleString('pt-BR', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                    })}
                </small>
            </div>

            <div className="flex-shrink-0 mx-2" style={{ width: "120px" }}>
                <div className="d-flex align-items-center gap-1">
                    {getIconByType(movimento.tipo)}
                    <Badge bg={getBadgeVariant(movimento.tipo)} pill className="fs-9">
                        {movimento.tipoDescricao}
                    </Badge>
                </div>
            </div>

            <div className="flex-shrink-0 mx-2 text-end" style={{ width: "80px" }}>
                <span
                    className={
                        movimento.quantidade > 0
                            ? "text-success fw-bold"
                            : movimento.quantidade < 0
                                ? "text-danger fw-bold"
                                : "text-body-tertiary"
                    }
                >
                    {movimento.quantidade > 0 ? "+" : ""}
                    {movimento.quantidade}
                </span>
            </div>

            <div className="flex-shrink-0 mx-2 text-end" style={{ width: "80px" }}>
                <span className="fw-bold">{movimento.quantidadeNova}</span>
            </div>

            <div className="flex-shrink-0 mx-2" style={{ width: "80px" }}>
                <small className="text-body-tertiary">
                    {movimento.lote || "-"}
                </small>
            </div>

            <div className="flex-grow-1 mx-2">
                <div className="d-flex align-items-center gap-1">
                    <FontAwesomeIcon icon={faStore} className="text-body-tertiary" size="sm" />
                    <small>{movimento.empresaNome}</small>
                </div>
            </div>

            <div className="flex-shrink-0" style={{ width: "120px" }}>
                <small className="text-body-tertiary">{movimento.usuarioNome}</small>
            </div>
        </div>
    );
};

const HistoricoEstoqueTab: React.FC<HistoricoEstoqueTabProps> = ({ produtoId }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const { selectedIds: empresaSelecionadas } = useEmpresas();
    const { breakpoints } = useBreakpoints();
    const isMobile = breakpoints.down('md');

    const pageSize = 50; // Maior pageSize para melhor performance com virtualização
    const empresasQuery = empresaSelecionadas.join(',');

    const {
        data,
        isLoading,
        error,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        refetch
    } = useInfiniteQuery({
        queryKey: ["produto", "movimentos-estoque", produtoId, empresasQuery],
        queryFn: async ({ pageParam = 1 }) => {
            const { data } = await axios.get(
                `/api/produto/${produtoId}/movimentos-estoque?page=${pageParam}&pageSize=${pageSize}&empresas=${empresasQuery}`
            );
            return data as MovimentoEstoqueResponse;
        },
        getNextPageParam: (lastPage: MovimentoEstoqueResponse, allPages) => {
            return lastPage.hasMore ? allPages.length + 1 : undefined;
        },
        initialPageParam: 1,
        enabled: isExpanded && !!produtoId,
        staleTime: 5 * 60 * 1000, // 5 minutos
    });

    // Flatten all items from all pages
    const allMovimentos = useMemo(() => {
        return data?.pages?.flatMap(page => page.data) || [];
    }, [data]);

    const totalCount = data?.pages?.[0]?.total || 0;

    const handleToggle = () => {
        setIsExpanded(!isExpanded);
    };

    const loadMoreItems = () => {
        if (hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
        }
    };

    const renderContent = () => {
        if (isLoading) {
            return (
                <div className="d-flex justify-content-center p-4">
                    <Spinner animation="border" size="sm" />
                    <span className="ms-2">Carregando movimentos...</span>
                </div>
            );
        }

        if (error) {
            return (
                <div className="text-center p-4">
                    <p className="text-danger mb-3">Erro ao carregar movimentos de estoque</p>
                    <button
                        className="btn btn-outline-primary btn-sm"
                        onClick={() => refetch()}
                    >
                        Tentar novamente
                    </button>
                </div>
            );
        }

        if (allMovimentos.length === 0) {
            return (
                <div className="text-center p-4 text-body-tertiary">
                    <FontAwesomeIcon icon={faHistory} size="2x" className="mb-3" />
                    <p>Nenhum movimento de estoque encontrado</p>
                </div>
            );
        }

        // Calcula o número de itens (incluindo um extra para loading se necessário)
        const itemCount = hasNextPage ? allMovimentos.length + 1 : allMovimentos.length;

        // Header da tabela - com margem para compensar scrollbar
        const tableHeader = (
            <div className="d-flex align-items-center border-bottom border-translucent bg-body-secondary px-3 py-2 fw-bold"
                style={{ marginRight: itemCount > Math.floor(400 / 60) ? "17px" : "0" }}>
                <div style={{ width: "120px" }}>Data</div>
                <div className="mx-2" style={{ width: "120px" }}>Tipo</div>
                <div className="mx-2 text-end" style={{ width: "80px" }}>Qtd</div>
                <div className="mx-2 text-end" style={{ width: "80px" }}>Saldo</div>
                <div className="mx-2" style={{ width: "80px" }}>Lote</div>
                <div className="flex-grow-1 mx-2">Empresa</div>
                <div style={{ width: "120px" }}>Usuário</div>
            </div>
        );

        return (
            <>
                {!isMobile && tableHeader}
                <div style={{ height: "400px", border: "1px solid var(--phoenix-border-color)" }}>
                    <InfiniteLoader
                        isItemLoaded={(index) => !!allMovimentos[index]}
                        itemCount={itemCount}
                        loadMoreItems={loadMoreItems}
                        threshold={10} // Carrega mais quando estiver a 10 itens do fim
                    >
                        {({ onItemsRendered, ref }) => (
                            <List
                                ref={ref}
                                height={400}
                                width="100%"
                                itemCount={itemCount}
                                itemSize={isMobile ? 120 : 60} // Altura maior para mobile
                                onItemsRendered={onItemsRendered}
                                itemData={{
                                    items: allMovimentos,
                                    loadMoreItems,
                                    hasNextPage: !!hasNextPage,
                                    isLoading: isFetchingNextPage
                                }}
                            >
                                {MovimentoRow}
                            </List>
                        )}
                    </InfiniteLoader>
                </div>

                {totalCount > 0 && (
                    <div className="text-center mt-3">
                        <small className="text-body-tertiary">
                            Mostrando {allMovimentos.length} de {totalCount} movimentos
                            {isFetchingNextPage && " • Carregando mais..."}
                        </small>
                    </div>
                )}
            </>
        );
    };

    return (
        <Card className="mb-3">
            <Accordion>
                <Accordion.Item eventKey="0">
                    <Accordion.Header onClick={handleToggle}>
                        <div className="d-flex align-items-center gap-2">
                            <FontAwesomeIcon icon={faHistory} className="text-primary" />
                            <span>Histórico de Movimentos de Estoque</span>
                            {totalCount > 0 && (
                                <Badge bg="primary" pill className="ms-2">
                                    {totalCount}
                                </Badge>
                            )}
                        </div>
                    </Accordion.Header>
                    <Accordion.Body>
                        {isExpanded && renderContent()}
                    </Accordion.Body>
                </Accordion.Item>
            </Accordion>
        </Card>
    );
};

export default HistoricoEstoqueTab; 